import React, { useState } from 'react';

import MiniOccupancyBar from './MiniOccupancyBar';
import BedChip from './BedChip';

// Long-list rule: a ward with MORE than this many beds starts COLLAPSED; a room
// shows at most this many tiles before a "+n more" reveal.
const COLLAPSE_THRESHOLD = 30;
const ROOM_TRUNCATE_AT = 12;

function bedMatchesQuery(bed, wardName, roomName, q) {
  if (!q) return true;
  return [bed.label, bed.patient_name, bed.ip_number, wardName, roomName]
    .some((v) => (v || '').toLowerCase().includes(q));
}

/**
 * WardGroup (C2 / FT5 + FT8) — one collapsible ward section of the grid.
 *
 * - COUNTS come ONLY from the shared `stats` prop (selectOccupancyStats) — the
 *   header count and the MiniOccupancyBar never re-derive from the bed list
 *   (single-source-of-truth; prevents a header/bar drift).
 * - A ward with >30 beds starts COLLAPSED (long-list rule); ≤30 expanded. The
 *   collapse can be controlled (search auto-expands a matching ward).
 * - The state filter DIMS non-matching tiles (`dimState`) — it never removes a
 *   tile, so the layout + header counts stay whole-truth.
 * - A room truncates to 12 tiles with a "+n more" reveal; a search shows all
 *   matches (no truncation while filtering).
 */
export default function WardGroup({
  ward,
  rooms = [],
  stats,
  tags = null,
  onBedClick,
  recentBedIds,
  collapsed: collapsedProp,
  onToggle,
  dimState = '',
  query = '',
}) {
  const total = stats?.total ?? 0;
  const occupied = stats?.byState?.OCCUPIED ?? 0;
  const [selfCollapsed, setSelfCollapsed] = useState(total > COLLAPSE_THRESHOLD);
  const [expandedRooms, setExpandedRooms] = useState(() => new Set());
  const q = (query || '').trim().toLowerCase();
  // A search hit auto-expands the ward regardless of the collapse default.
  const collapsed = q ? false : (collapsedProp != null ? collapsedProp : selfCollapsed);

  const toggle = () => (onToggle ? onToggle() : setSelfCollapsed((c) => !c));
  const revealRoom = (roomId) =>
    setExpandedRooms((prev) => new Set(prev).add(roomId));

  return (
    <section
      data-testid={`ward-${ward.ward_id}`}
      className="mb-4 rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900"
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`ward-hd-${ward.ward_id}`}
        className="sticky top-0 z-10 flex w-full items-center gap-2 rounded-t-xl border-b border-slate-200 bg-slate-50 px-3 py-2 text-left dark:border-slate-700 dark:bg-slate-800"
      >
        <span aria-hidden="true" className="text-slate-500">{collapsed ? '▸' : '▾'}</span>
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{ward.name}</h3>
        {tags}
        <span
          data-testid={`ward-count-${ward.ward_id}`}
          className="ml-auto text-xs font-medium text-slate-600 dark:text-slate-300"
        >
          {occupied}/{total} occupied
        </span>
      </button>

      {!collapsed && (
        <div className="px-3 pb-3 pt-2">
          <div className="mb-3">
            <MiniOccupancyBar byState={stats?.byState} total={total} />
          </div>

          {rooms.map(({ room, beds }) => {
            const visible = beds.filter((b) => bedMatchesQuery(b, ward.name, room.name, q));
            if (q && visible.length === 0) return null;
            const expanded = q || expandedRooms.has(room.room_id);
            const shown = expanded ? visible : visible.slice(0, ROOM_TRUNCATE_AT);
            const more = visible.length - shown.length;
            return (
              <div key={room.room_id} className="mb-3 last:mb-0">
                <div className="mb-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-400">
                  {room.name}
                  <span className="text-slate-400 dark:text-slate-500"> · {beds.length} beds</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {shown.map((bed) => (
                    <div
                      key={bed.bed_id}
                      className={dimState && bed.state !== dimState ? 'opacity-40' : ''}
                    >
                      <BedChip
                        bed={bed}
                        onClick={() => onBedClick?.(bed)}
                        justUpdated={recentBedIds?.has?.(bed.bed_id)}
                        highlight={!!q}
                      />
                    </div>
                  ))}
                  {more > 0 && (
                    <button
                      type="button"
                      onClick={() => revealRoom(room.room_id)}
                      data-testid={`room-more-${room.room_id}`}
                      className="w-[168px] min-h-[70px] rounded-lg border border-dashed border-teal-400 bg-white text-sm font-semibold text-teal-700 hover:bg-teal-50 dark:bg-slate-900 dark:text-teal-300 dark:hover:bg-slate-800"
                    >
                      + {more} more beds
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
